import React from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "@/components/Icon";
import { Badge } from "@/components/Badge";
import { price, getAvatar, quantity } from "@/utils/format";
import type { AiUsageStatsData } from "@/pages/AiConsumption/types";

interface AiConsumptionBreakdownsProps {
    stats: AiUsageStatsData;
}

export const AiConsumptionBreakdowns: React.FC<
    AiConsumptionBreakdownsProps
> = ({ stats }) => {
    const { t } = useTranslation("global");

    const maxUserTokens = Math.max(
        ...stats.by_user.map((u) => u.total_tokens),
        1
    );
    const maxModelTokens = Math.max(
        ...stats.by_model.map((m) => m.total_tokens),
        1
    );
    const maxContextTokens = Math.max(
        ...stats.by_context_type.map((c) => c.total_tokens),
        1
    );

    const getContextBadgeLabel = (contextType: string): string => {
        const key = `ai_consumption.context_types.${contextType}`;
        const translated = t(key);
        if (translated && translated !== key) {
            return translated;
        }
        return contextType;
    };

    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 w-full">
            {/* By User */}
            <div className="bg-box rounded-2xl border border-box p-5 shadow-xs flex flex-col">
                <div className="flex items-center justify-center gap-2 mb-4">
                    <div className="p-2 rounded-xl bg-primary/10 text-primary">
                        <Icon value="users" size={18} />
                    </div>
                    <div>
                        <h3 className="text-sm font-bold text-slate-800 dark:text-zinc-200">
                            {t("ai_consumption.users_stats")}
                        </h3>
                    </div>
                </div>

                <div className="space-y-3 flex-1 overflow-y-auto max-h-[300px] pr-1">
                    {stats.by_user.length === 0 ? (
                        <div className="text-xs text-slate-400 dark:text-zinc-500 text-center py-6">
                            {t("ai_consumption.no_data")}
                        </div>
                    ) : (
                        stats.by_user.map((user) => {
                            const percent = Math.min(
                                100,
                                Math.round(
                                    (user.total_tokens / maxUserTokens) * 100
                                )
                            );
                            return (
                                <div
                                    key={user.user_id ?? "unknown"}
                                    className="p-2.5 rounded-xl bg-slate-50 dark:bg-dark-border/40 border border-slate-100 dark:border-white/5 space-y-1.5"
                                >
                                    <div className="flex items-center justify-between gap-2">
                                        <div className="flex items-center gap-2 min-w-0">
                                            <div className="w-7 h-7 rounded-full bg-primary/15 text-primary text-[11px] font-bold flex items-center justify-center shrink-0">
                                                {getAvatar(user.name)}
                                            </div>
                                            <div className="min-w-0">
                                                <div className="text-xs font-semibold text-slate-800 dark:text-zinc-200 truncate">
                                                    {user.name}
                                                </div>
                                                {user.email && (
                                                    <div className="text-[10px] text-slate-400 dark:text-zinc-500 truncate">
                                                        {user.email}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div className="text-right shrink-0">
                                            <div className="text-xs font-bold text-slate-800 dark:text-zinc-200">
                                                {quantity(user.total_tokens)}{" "}
                                                tok
                                            </div>
                                            <div className="text-[10px] font-semibold text-emerald-600 dark:text-emerald-400">
                                                {price(
                                                    user.total_cost,
                                                    "euro",
                                                    "0.00000€",
                                                    5
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Progress Bar */}
                                    <div className="w-full bg-slate-200 dark:bg-white/10 h-1.5 rounded-full overflow-hidden">
                                        <div
                                            className="bg-primary h-full rounded-full transition-all duration-500"
                                            style={{ width: `${percent}%` }}
                                        />
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>

            {/* By Provider & Model */}
            <div className="bg-box rounded-2xl border border-box p-5 shadow-xs flex flex-col">
                <div className="flex items-center justify-center gap-2 mb-4">
                    <div className="p-2 rounded-xl bg-blue-500/10 text-blue-600 dark:text-blue-400">
                        <Icon value="bot" size={18} />
                    </div>
                    <div>
                        <h3 className="text-sm font-bold text-slate-800 dark:text-zinc-200">
                            {t("ai_consumption.models_stats")}
                        </h3>
                    </div>
                </div>

                <div className="space-y-2.5 flex-1 overflow-y-auto max-h-[300px] pr-1">
                    {stats.by_model.length === 0 ? (
                        <div className="text-xs text-slate-400 dark:text-zinc-500 text-center py-6">
                            {t("ai_consumption.no_data")}
                        </div>
                    ) : (
                        stats.by_model.map((model) => {
                            const percent = Math.min(
                                100,
                                Math.round(
                                    (model.total_tokens / maxModelTokens) * 100
                                )
                            );
                            return (
                                <div
                                    key={`${model.provider}-${model.model}`}
                                    className="p-2.5 rounded-xl bg-slate-50 dark:bg-dark-border/40 border border-slate-100 dark:border-white/5 space-y-1.5"
                                >
                                    <div className="flex items-center justify-between gap-2">
                                        <div className="min-w-0">
                                            <div className="flex items-center gap-1.5 flex-wrap">
                                                <Badge variant="info" size="xs">
                                                    {model.provider}
                                                </Badge>
                                                <span className="text-xs font-semibold text-slate-800 dark:text-zinc-200 truncate">
                                                    {model.model}
                                                </span>
                                            </div>
                                            <div className="text-[10px] text-slate-400 dark:text-zinc-500 mt-0.5">
                                                {quantity(model.requests_count)}{" "}
                                                {model.requests_count === 1
                                                    ? t(
                                                          "ai_consumption.request_singular"
                                                      )
                                                    : t(
                                                          "ai_consumption.requests"
                                                      )}
                                            </div>
                                        </div>
                                        <div className="text-right shrink-0">
                                            <div className="text-xs font-bold text-slate-800 dark:text-zinc-200">
                                                {quantity(model.total_tokens)}{" "}
                                                tok
                                            </div>
                                            <div className="text-[10px] font-semibold text-emerald-600 dark:text-emerald-400">
                                                {price(
                                                    model.total_cost,
                                                    "euro",
                                                    "0.00000€",
                                                    5
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Progress Bar */}
                                    <div className="w-full bg-slate-200 dark:bg-white/10 h-1.5 rounded-full overflow-hidden">
                                        <div
                                            className="bg-blue-500 h-full rounded-full transition-all duration-500"
                                            style={{ width: `${percent}%` }}
                                        />
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>

            {/* By Action / Context Type */}
            <div className="bg-box rounded-2xl border border-box p-5 shadow-xs flex flex-col">
                <div className="flex items-center justify-center gap-2 mb-4">
                    <div className="p-2 rounded-xl bg-purple-500/10 text-purple-600 dark:text-purple-400">
                        <Icon value="layers" size={18} />
                    </div>
                    <div>
                        <h3 className="text-sm font-bold text-slate-800 dark:text-zinc-200">
                            {t("ai_consumption.context_stats")}
                        </h3>
                    </div>
                </div>

                <div className="space-y-2.5 flex-1 overflow-y-auto max-h-[300px] pr-1">
                    {stats.by_context_type.length === 0 ? (
                        <div className="text-xs text-slate-400 dark:text-zinc-500 text-center py-6">
                            {t("ai_consumption.no_data")}
                        </div>
                    ) : (
                        stats.by_context_type.map((ctx) => {
                            const percent = Math.min(
                                100,
                                Math.round(
                                    (ctx.total_tokens / maxContextTokens) * 100
                                )
                            );
                            return (
                                <div
                                    key={ctx.context_type}
                                    className="p-2.5 rounded-xl bg-slate-50 dark:bg-dark-border/40 border border-slate-100 dark:border-white/5 space-y-1.5"
                                >
                                    <div className="flex items-center justify-between gap-2">
                                        <div className="min-w-0">
                                            <div className="text-xs font-semibold text-slate-800 dark:text-zinc-200 capitalize">
                                                {getContextBadgeLabel(
                                                    ctx.context_type
                                                )}
                                            </div>
                                            <div className="text-[10px] text-slate-400 dark:text-zinc-500 mt-0.5">
                                                {quantity(ctx.requests_count)}{" "}
                                                {ctx.requests_count === 1
                                                    ? t(
                                                          "ai_consumption.request_singular"
                                                      )
                                                    : t(
                                                          "ai_consumption.requests"
                                                      )}
                                            </div>
                                        </div>
                                        <div className="text-right shrink-0">
                                            <div className="text-xs font-bold text-slate-800 dark:text-zinc-200">
                                                {quantity(ctx.total_tokens)} tok
                                            </div>
                                            <div className="text-[10px] font-semibold text-emerald-600 dark:text-emerald-400">
                                                {price(
                                                    ctx.total_cost,
                                                    "euro",
                                                    "0.00000€",
                                                    5
                                                )}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Progress Bar */}
                                    <div className="w-full bg-slate-200 dark:bg-white/10 h-1.5 rounded-full overflow-hidden">
                                        <div
                                            className="bg-purple-500 h-full rounded-full transition-all duration-500"
                                            style={{ width: `${percent}%` }}
                                        />
                                    </div>
                                </div>
                            );
                        })
                    )}
                </div>
            </div>
        </div>
    );
};
